import React, { Suspense, useEffect } from 'react';

import { css } from '@emotion/css';
import { Theme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet } from '@tanstack/react-router';

import {
  PatientNavigation,
  PatientNavigationSkeleton,
} from '@/components/navigation/PatientNavigation';
import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles';
import { HandledErrorPage } from '@/pages/HandledErrorPage.tsx';
import { Queries } from '@/queries/Queries.ts';
import { QueryAwait } from '@/queries/QueryAwait.tsx';
import { ScreenMax } from '@/theme/responsive.ts';
import { CenteredLoader } from '@/uiKit/atoms/pending/CenteredLoader.tsx';
import { ChatMessages } from '@/uiKit/organisms/ChatMessaging/ChatMessages.tsx';
import { ChatMessagingProvider } from '@/uiKit/organisms/ChatMessaging/ChatMessagingState.ts';

export const PractitionerManagePatientLayout: React.FC = () => {
  const styles = useStyles(makeStyles);
  const patientId = usePatientIdFromURL();
  const queryClient = useQueryClient();
  useEffect(() => {
    Queries.condition.useCondition.prefetch(queryClient, patientId);
    Queries.practitioner.usePatient.prefetch(queryClient, patientId);
  }, [queryClient, patientId]);

  return (
    <ChatMessagingProvider isOpen={false} visibility="all">
      <div className={styles.chatAside}>
        <div className={styles.content}>
          <Suspense fallback={<PatientNavigationSkeleton />}>
            <PatientNavigation />
          </Suspense>
          <section id="manage-patient-layout-main" className={styles.main}>
            <QueryAwait
              errorFallback={HandledErrorPage}
              pendingFallback={CenteredLoader}
            >
              <Outlet />
            </QueryAwait>
          </section>
        </div>
        <ChatMessages className={styles.chat} />
      </div>
    </ChatMessagingProvider>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1',
    overflow: 'hidden',
  }),
  main: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(12)} 2vw;
  `,
  chatAside: css({
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  }),
  chat: css({
    [ScreenMax.MD]: {
      width: '100% !important',
    },
    height: '100vh',
    position: 'sticky',
    top: 0,
  }),
});
